import React, { createContext, useEffect, useReducer } from 'react';

// third party
// import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';
import { dispatch as storeDispatch } from 'store';

// project imports
import Loader from 'ui-component/Loader';
import { fetcher } from 'utils/axios';
import axios from 'axios';

// types
import { KeyedObject } from 'types';
import { InitialLoginContextProps, JWTContextType } from 'types/auth';
import { setUserData } from 'store/slices/user';
import { setCompanyData } from 'store/slices/company';
import { getDataOnLoad } from 'services/dataLoader';
import { fetchGetApplicationUserProfile, fetchLogin, getFunnerUserServerContext } from 'api/auth';
import { useSnackbar } from 'notistack';
import useLocalization from 'hooks/useLocalization';
import { CurrentUser } from 'types/user';
import { CompanyModel } from 'types/company/company';
import { DEV_SKIP_AUTH } from 'config';

// const chance = new Chance();

// constant
const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  token: null,
};

const MOCK_DEV_USER: CurrentUser = {
  Email: 'local@dev',
  CompanyId: 'local-dev',
  FirstName: 'Local',
  LastName: 'Preview',
  FullName: 'Local Preview',
  IsActive: true,
  Role: 'admin',
  CompanyType: 'standalone',
  PhoneNumber: '0500000000',
  IsShowAllChatUsers: true,
  CompanyName: 'Local Preview',
  Udid: 'local-dev',
};

const MOCK_DEV_COMPANY: CompanyModel = {
  Id: 1,
  CompanyName: 'Local Preview',
  CompanyEmail: 'local@dev',
  CompanyCountry: '',
  CompanyCity: '',
  CompanyLogo: '',
  PhoneNumberID: '',
  BusinessAccountID: '',
  WhatsAppToken: '',
  CompanyId: 'local-dev',
  DisplayPhoneNumber: '',
  AdminTokenId: '',
  DefaultChatUserContact: null,
  ConversationContactManagerMode: '',
  IncomingMessageWebhook: '',
  IncomingChatUserWebhook: '',
  IncomingContactStatusChangeOpenWebhook: '',
  IncomingContactStatusChangeWebhook: '',
  IncomingContactStatusChangeCloseWebhook: '',
  MessageEncryptionKey: '',
  LinkToObjectNum: '',
  CompanyType: 'standalone',
  ChatUserContactChangeWebhook: '',
  RegistrationStatus: '',
  RegistrationMessage: '',
};

function verifyToken(token: string): boolean {
  if (!token) {
    return false;
  }

  const decoded: KeyedObject = jwtDecode(token);

  // Ensure 'exp' exists and compare it to the current timestamp
  if (!decoded.exp) {
    throw new Error("Token does not contain 'exp' property.");
  }

  return decoded.exp > Date.now() / 1000;
}

function setSession(token?: string | null): void {
  if (token) {
    localStorage.setItem('whatsapp-token', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('whatsapp-token');
    localStorage.removeItem('serverContext');
    delete axios.defaults.headers.common.Authorization;
  }
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export function JWTProvider({ children }: { children: React.ReactElement }) {
  const { currentLocale } = useLocalization();
  const { enqueueSnackbar } = useSnackbar();

  const [state, dispatch] = useReducer(accountReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        if (DEV_SKIP_AUTH) {
          setSession(null);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: MOCK_DEV_USER,
              token: null,
            },
          });
          storeDispatch(setUserData(MOCK_DEV_USER));
          storeDispatch(setCompanyData(MOCK_DEV_COMPANY));
          return;
        }

        const token = window.localStorage.getItem('whatsapp-token');
        if (token && verifyToken(token)) {
          setSession(token);
          const response = await fetcher('get', '/api/auth/getApplicationUserProfile', 'history');
          if (response?.success) {
            const { data: user } = response;
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user,
                token,
              },
            });
            storeDispatch(setUserData(user));

            getDataOnLoad();
          } else {
            // If fetcher returns null or response is not successful, logout
            console.warn('Failed to get user profile:', response);
            dispatch({
              type: LOGOUT,
            });
            storeDispatch(setUserData(null));
          }
        } else {
          dispatch({
            type: LOGOUT,
          });
          storeDispatch(setUserData(null));
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT,
        });
        storeDispatch(setUserData(null));
      }
    };

    init();
  }, []);

  const login = React.useCallback(
    async (phone: string, password: string) => {
      try {
        const serverContextRes = await getFunnerUserServerContext(phone);
        const serverContext = serverContextRes?.serverContext || 0;
        localStorage.setItem('serverContext', serverContext);

        const loginResData = await fetchLogin(phone, password);

        // בדוק אם התשובה תקינה
        if (!loginResData) {
          return { success: false, error: currentLocale.auth.notifications.login.error };
        }

        // אם הצליח - יש token ב-data
        if (loginResData.success && loginResData.data) {
          const token = loginResData.data;

          // מבצע לוגאין מלא
          setSession(token);
          const userResData = await fetchGetApplicationUserProfile();
          if (userResData?.success) {
            const { data: user } = userResData;
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user,
                token,
              },
            });
            storeDispatch(setUserData(user));
            getDataOnLoad();
            return { success: true };
          } else {
            return { success: false, error: 'Failed to get user profile' };
          }
        }

        // אם נכשל - בודק את סוג השגיאה
        if (!loginResData.success && loginResData.error) {
          if (loginResData.error === 'otp_not_verified') {
            // מעביר למסך OTP עם context=login
            enqueueSnackbar(currentLocale.auth.verifyOTP.youMustVerifyOtp, {
              variant: 'warning',
            });

            setTimeout(() => {
              if (typeof window !== 'undefined' && window.history.pushState) {
                window.history.pushState(
                  {},
                  '',
                  `/code-verification?phone=${phone}&sendOnLoad=true&context=login`,
                );
                window.dispatchEvent(new PopStateEvent('popstate'));
              } else {
                window.location.href = `/code-verification?phone=${phone}&sendOnLoad=true&context=login`;
              }
            }, 200);
            return { success: true }; // מחזיר success כדי שלא יציג שגיאה במסך הלוגאין
          } else if (loginResData.error === 'user_or_password_wrong') {
            // מחזיר שגיאה שתצוג במסך הלוגאין
            return {
              success: false,
              error: currentLocale.auth.notifications.login.error,
            };
          }
        }

        // fallback - אם אין error ספציפי
        return { success: false, error: currentLocale.auth.notifications.login.error };
      } catch (error) {
        console.error('Login error:', error);
        return { success: false, error: currentLocale.auth.notifications.login.error };
      }
    },
    [
      currentLocale.auth.notifications.login.error,
      currentLocale.auth.verifyOTP.youMustVerifyOtp,
      enqueueSnackbar,
    ],
  );

  // const register = React.useCallback(
  //   async (email: string, password: string, firstName: string, lastName: string) => {
  //     const id = chance.bb_pin();
  //     const response = await axiosHistoryServices.post('/api/account/register', {
  //       id,
  //       email,
  //       password,
  //       firstName,
  //       lastName,
  //     });
  //     let users = response.data;

  //     if (
  //       window.localStorage.getItem('users') !== undefined &&
  //       window.localStorage.getItem('users') !== null
  //     ) {
  //       const localUsers = window.localStorage.getItem('users');
  //       users = [
  //         ...JSON.parse(localUsers!),
  //         {
  //           id,
  //           email,
  //           password,
  //           name: `${firstName} ${lastName}`,
  //         },
  //       ];
  //     }

  //     window.localStorage.setItem('users', JSON.stringify(users));
  //   },
  //   [],
  // );

  const logout = React.useCallback(() => {
    setSession(null);
    dispatch({ type: LOGOUT });
    storeDispatch(setUserData(null));
  }, []);

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  // @ts-ignore
  return <JWTContext.Provider value={{ ...state, login, logout }}>{children}</JWTContext.Provider>;
}

export default JWTContext;
